// frontend/src/features/campaign/components/common/GovernanceApprovalRequiredBanner.js
import React, { useState } from 'react';

const PRESET_REVISION_REASONS = [
  'Targeting & ICP Mismatch',
  'Budget Cap Adjustment Needed',
  'Refine Value Proposition & CTA',
  'PDPA / Regulatory Consent Required',
  'Adjust Touchpoint Cadence & Channels',
  'Update Proof Points / Case Studies',
];

export function RequestChangesModal({
  isOpen,
  onClose,
  campaign,
  currentUserName = 'Sales Manager',
  userRoleLabel = 'Admin',
  onSubmitRevisionRequest,
}) {
  const [selectedTags, setSelectedTags] = useState([]);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleToggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    if (error) setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!revisionNotes.trim() && selectedTags.length === 0) {
      setError('Please provide feedback or select at least one reason tag for the creator.');
      return;
    }

    const fullFeedback = [
      selectedTags.length > 0 ? `[Tags: ${selectedTags.join(', ')}]` : '',
      revisionNotes.trim(),
    ]
      .filter(Boolean)
      .join('\n\n');

    onSubmitRevisionRequest(fullFeedback, selectedTags);
    onClose();
  };

  return (
    <div className="cmp-modal-backdrop" onClick={onClose}>
      <div
        className="cmp-modal-container cmp-request-changes-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="cmp-modal-header">
          <div className="cmp-modal-header-left">
            <div className="cmp-modal-icon-badge warning">
              <i className="fas fa-undo-alt" />
            </div>
            <div>
              <h3 className="cmp-modal-title">Request Campaign Revisions</h3>
              <p className="cmp-modal-subtitle">
                Return campaign to creator for adjustments before governance activation.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="cmp-modal-close-btn"
            onClick={onClose}
            title="Close modal"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="cmp-request-changes-form">
          <div className="cmp-modal-body cmp-request-changes-body">
            {error && (
              <div className="cmp-modal-error-alert">
                <i className="fas fa-exclamation-circle" />
                <span>{error}</span>
              </div>
            )}

            {/* Campaign Info Strip */}
            <div className="cmp-rev-context-strip">
              <div className="cmp-rev-ctx-item">
                <span className="cmp-rev-ctx-label">Campaign:</span>
                <strong className="cmp-rev-ctx-val">{campaign?.name || 'Untitled Campaign'}</strong>
              </div>
              <div className="cmp-rev-ctx-item">
                <span className="cmp-rev-ctx-label">Creator:</span>
                <span className="cmp-rev-ctx-val">{campaign?.createdBy || 'Team Member'}</span>
              </div>
              <div className="cmp-rev-ctx-item">
                <span className="cmp-rev-ctx-label">Budget:</span>
                <span className="cmp-rev-ctx-val">{campaign?.budget || 'RM 25,000'}</span>
              </div>
            </div>

            {/* Quick Preset Tags */}
            <div className="cmp-field-group">
              <label className="cmp-field-label">
                <span className="cmp-field-label-text">
                  <i className="fas fa-tags" /> Quick Revision Categories:
                </span>
              </label>
              <div className="cmp-rev-tags-wrap">
                {PRESET_REVISION_REASONS.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      className={`cmp-rev-tag-btn ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleToggleTag(tag)}
                    >
                      <i className={`fas ${isSelected ? 'fa-check' : 'fa-tag'}`} /> {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Feedback Notes Textarea */}
            <div className="cmp-field-group" style={{ marginTop: '4px' }}>
              <label className="cmp-field-label">
                <span className="cmp-field-label-text">
                  <i className="fas fa-comment-dots" /> Specific Feedback & Action Items for Creator
                </span>
                <span className="text-danger">*</span>
              </label>
              <textarea
                className="cmp-field-textarea cmp-rev-textarea"
                rows={3}
                placeholder="e.g. Please refine persona value propositions in Step 3, ensure target market matches Selangor hub, and verify budget caps before resubmission."
                value={revisionNotes}
                onChange={(e) => {
                  setRevisionNotes(e.target.value);
                  if (error) setError('');
                }}
                autoFocus
              />
            </div>

            {/* Reviewer Attribution Line */}
            <div className="cmp-rev-attribution">
              <i className="fas fa-shield-alt" />
              <span>
                This change request will be recorded by <strong>{currentUserName}</strong> (
                {userRoleLabel}) and the campaign status will change to{' '}
                <span className="cmp-status-inline-tag changes-requested">Changes Requested</span>.
              </span>
            </div>
          </div>

          {/* Modal Footer Actions */}
          <div className="cmp-modal-footer">
            <div className="cmp-modal-footer-left">
              <span className="cmp-rev-hint-text">
                Creator can edit and resubmit upon receiving revisions
              </span>
            </div>
            <div className="cmp-modal-footer-right">
              <button type="button" className="cmp-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="cmp-btn-warning-submit">
                <i className="fas fa-paper-plane" /> Submit Change Request
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const PRESET_MEMBER_CHANGE_REASONS = [
  'Targeting & ICP Refinement',
  'Offer Matrix & Messaging Update',
  'Channel & Cadence Adjustment',
  'Budget & Timeline Revision',
  'Lead Cohort List Update',
  'Proof Points & Objection Polish',
];

const CAMPAIGN_SECTIONS_LIST = [
  { id: 'step1', label: 'Step 1: Brief & Goals', icon: 'fa-bullseye' },
  { id: 'step2', label: 'Step 2: Target Cohorts & Leads', icon: 'fa-users' },
  { id: 'step3', label: 'Step 3: Offer & Messaging Matrix', icon: 'fa-layer-group' },
  { id: 'step4', label: 'Step 4: Outreach Cadence & Channels', icon: 'fa-paper-plane' },
  { id: 'step5', label: 'Step 5: Forecast & KPIs Budget', icon: 'fa-chart-line' },
];

export function MemberSubmitChangeRequestModal({
  isOpen,
  onClose,
  campaign,
  formData,
  currentUserName = 'Team Member',
  userRoleLabel = 'Sales Member',
  onSubmitChangeRequest,
}) {
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [changeNotes, setChangeNotes] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleToggleSection = (sectionLabel) => {
    setSelectedSections((prev) =>
      prev.includes(sectionLabel) ? prev.filter((s) => s !== sectionLabel) : [...prev, sectionLabel]
    );
    if (error) setError('');
  };

  const handleToggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    if (error) setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedSections.length === 0) {
      setError('Please select at least one modified campaign section.');
      return;
    }
    if (selectedTags.length === 0) {
      setError('Please select at least one change category tag.');
      return;
    }

    const sectionStr = `[Modified Sections: ${selectedSections.join(', ')}]`;
    const tagStr = `[Categories: ${selectedTags.join(', ')}]`;
    const reasonStr = changeNotes.trim()
      ? `Reason: ${changeNotes.trim()}`
      : `Reason: Modified ${selectedSections.join(', ')} (${selectedTags.join(', ')}).`;

    const fullNotes = [sectionStr, tagStr, reasonStr].filter(Boolean).join('\n\n');

    onSubmitChangeRequest(fullNotes, {
      fullNotes,
      selectedSections,
      selectedTags,
      changeNotes: changeNotes.trim(),
    });
    onClose();
  };

  return (
    <div className="cmp-modal-backdrop" onClick={onClose}>
      <div
        className="cmp-modal-container cmp-request-changes-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="cmp-modal-header">
          <div className="cmp-modal-header-left">
            <div className="cmp-modal-icon-badge blue">
              <i className="fas fa-paper-plane" />
            </div>
            <div>
              <h3 className="cmp-modal-title">Submit Campaign Change Request</h3>
              <p className="cmp-modal-subtitle">
                Notify workspace managers of your modifications for review and re-certification.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="cmp-modal-close-btn"
            onClick={onClose}
            title="Close modal"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="cmp-request-changes-form">
          <div className="cmp-modal-body cmp-request-changes-body">
            {/* Error Alert Box */}
            {error && (
              <div className="cmp-modal-error-alert">
                <i className="fas fa-exclamation-circle" />
                <span>{error}</span>
              </div>
            )}

            {/* 1. Select Modified Sections (Compulsory) */}
            <div className="cmp-field-group">
              <label className="cmp-field-label">
                <span className="cmp-field-label-text">
                  <i className="fas fa-th-list" /> Which section(s) were modified?{' '}
                  <span className="cmp-rev-hint-sub">(Multi-select)</span>{' '}
                  <span className="text-danger">*</span>
                </span>
              </label>
              <div
                className={`cmp-sec-select-grid ${error && selectedSections.length === 0 ? 'has-error' : ''}`}
              >
                {CAMPAIGN_SECTIONS_LIST.map((sec) => {
                  const isChecked = selectedSections.includes(sec.label);
                  return (
                    <button
                      key={sec.id}
                      type="button"
                      className={`cmp-sec-select-pill ${isChecked ? 'selected' : ''}`}
                      onClick={() => handleToggleSection(sec.label)}
                    >
                      <i className={`fas ${isChecked ? 'fa-check-circle' : sec.icon}`} />
                      <span>{sec.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2. Quick Preset Tags (Compulsory) */}
            <div className="cmp-field-group" style={{ marginTop: '2px' }}>
              <label className="cmp-field-label">
                <span className="cmp-field-label-text">
                  <i className="fas fa-tags" /> Change Category Tags{' '}
                  <span className="text-danger">*</span>
                </span>
              </label>
              <div
                className={`cmp-rev-tags-wrap ${error && selectedTags.length === 0 ? 'has-error' : ''}`}
              >
                {PRESET_MEMBER_CHANGE_REASONS.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      className={`cmp-rev-tag-btn ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleToggleTag(tag)}
                    >
                      {isSelected && <i className="fas fa-check" />} {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 3. Feedback Notes Textarea (Optional) */}
            <div className="cmp-field-group" style={{ marginTop: '2px' }}>
              <label className="cmp-field-label">
                <span className="cmp-field-label-text">
                  <i className="fas fa-comment-dots" /> Reason for Changes & Modification Details{' '}
                  <span className="cmp-rev-hint-sub">(Optional)</span>
                </span>
              </label>
              <textarea
                className="cmp-field-textarea cmp-rev-textarea"
                rows={3}
                placeholder="Explain what was changed and why (e.g. Added 5 newly qualified CTO leads from CRM, updated Step 3 value proposition for FinTech, and refined Day 7 cadence interval)..."
                value={changeNotes}
                onChange={(e) => {
                  setChangeNotes(e.target.value);
                  if (error) setError('');
                }}
                autoFocus
              />
            </div>
          </div>

          {/* Modal Footer Actions */}
          <div className="cmp-modal-footer" style={{ justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="cmp-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cmp-btn-primary">
              <i className="fas fa-paper-plane" /> Submit Change Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ExecutiveSignContractModal({
  isOpen,
  onClose,
  campaign,
  formData,
  approvedGates = new Set([
    'brand_voice',
    'commercial',
    'compliance_deliverability',
    'pdpa_privacy',
  ]),
  currentUserName = 'Alex Wong',
  userRoleLabel = 'Workspace Owner',
  workspaceName = 'Lead Huntrix Production Workspace',
  onConfirmApproval,
}) {
  const [signatureMode, setSignatureMode] = useState('draw');
  const [typedSignature, setTypedSignature] = useState(
    currentUserName ? `${currentUserName} (${userRoleLabel})` : 'Alex Wong (Workspace Owner)'
  );
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [error, setError] = useState('');
  const canvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);

  React.useEffect(() => {
    if (isOpen) {
      setError('');
      setHasDrawnSignature(false);
      setSignatureData(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Extract member change notes if available
  const rawNote =
    campaign?.submissionNotes ||
    formData?.submissionNotes ||
    campaign?.strategy?.submissionNotes ||
    '';
  const extractedSectionsMatch = (rawNote.match(/\[Modified Sections?:\s*([^\]]+)\]/i) || [])[1];
  let rawSections =
    campaign?.modifiedSections ||
    formData?.modifiedSections ||
    campaign?.strategy?.modifiedSections ||
    campaign?.governance?.modifiedSections ||
    formData?.governance?.modifiedSections ||
    (extractedSectionsMatch ? extractedSectionsMatch.split(',').map((s) => s.trim()) : []);
  let sectionsList = Array.isArray(rawSections)
    ? rawSections.filter(Boolean)
    : typeof rawSections === 'string'
      ? rawSections
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const extractedTagsMatch = (rawNote.match(/\[(?:Categories|Tags):\s*([^\]]+)\]/i) || [])[1];
  let rawTags =
    campaign?.changeReasonTags ||
    formData?.changeReasonTags ||
    campaign?.strategy?.changeReasonTags ||
    campaign?.governance?.changeReasonTags ||
    formData?.governance?.changeReasonTags ||
    (extractedTagsMatch ? extractedTagsMatch.split(',').map((s) => s.trim()) : []);
  let tagsList = Array.isArray(rawTags)
    ? rawTags.filter(Boolean)
    : typeof rawTags === 'string'
      ? rawTags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  let memberReason = rawNote
    .replace(/\[Modified Sections?:\s*[^\]]+\]/gi, '')
    .replace(/\[(?:Categories|Tags):\s*[^\]]+\]/gi, '')
    .replace(/(?:^|\n)\s*Reason:\s*/gi, '')
    .trim();

  if (
    memberReason.toLowerCase().includes('ai-generated outbound strategy configuration') ||
    memberReason.toLowerCase().includes('automated initial creation')
  ) {
    memberReason = '';
  }

  const memberAuthor =
    campaign?.changeRequestedBy ||
    campaign?.governance?.changeRequestedBy ||
    campaign?.strategy?.changeRequestedBy ||
    campaign?.createdBy ||
    'Team Member';

  const isPreviouslyApproved = Boolean(
    campaign?.approvedAt || campaign?.governance?.isCertified || campaign?.governance?.approvedAt
  );

  const isMemberChangeRequest = Boolean(
    campaign?.changeRequestedBy ||
    campaign?.changeRequestedAt ||
    campaign?.governance?.changeRequestedBy ||
    campaign?.strategy?.changeRequestedBy ||
    sectionsList.length > 0 ||
    tagsList.length > 0 ||
    /\[Modified Sections?:\s*[^\]]+\]/i.test(rawNote) ||
    /\[(?:Categories|Tags):\s*[^\]]+\]/i.test(rawNote)
  );

  const isReApproval =
    isMemberChangeRequest || isPreviouslyApproved || campaign?.status === 'CHANGES_REQUESTED';

  // Canvas Drawing Handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL());
      setHasDrawnSignature(true);
      if (error) setError('');
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSignatureData(null);
    setHasDrawnSignature(false);
  };

  const handleSignAndApprove = (e) => {
    e.preventDefault();
    if (signatureMode === 'draw' && !hasDrawnSignature) {
      setError('Please draw your digital signature on the pad to certify.');
      return;
    }
    if (signatureMode === 'type' && !typedSignature.trim()) {
      setError('Please enter your full name & title for the typed signature.');
      return;
    }

    const payload = {
      approvedGates: ['brand_voice', 'commercial', 'compliance_deliverability', 'pdpa_privacy'],
      allGatesApproved: true,
      isCertified: true,
      signatureType: signatureMode,
      signatureData: signatureMode === 'draw' ? signatureData : null,
      typedSignature: signatureMode === 'type' ? typedSignature.trim() : null,
      signedBy: `${currentUserName} (${userRoleLabel || 'Workspace Owner'})`,
      approverRole: userRoleLabel || 'Workspace Owner',
      isReapproval: isReApproval,
    };

    onConfirmApproval(payload);
    onClose();
  };

  const isFormReady = signatureMode === 'draw' ? hasDrawnSignature : Boolean(typedSignature.trim());

  return (
    <div className="cmp-modal-backdrop" onClick={onClose}>
      <div
        className="cmp-modal-container cmp-exec-sign-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '520px' }}
      >
        {/* Modal Header */}
        <div className="cmp-modal-header">
          <div className="cmp-modal-header-left">
            <div className="cmp-modal-icon-badge blue">
              <i className="fas fa-pen-nib" />
            </div>
            <div>
              <h3 className="cmp-modal-title">
                {isReApproval ? 'Re-Sign & Authorize Campaign' : 'Sign & Authorize Campaign'}
              </h3>
              <p className="cmp-modal-subtitle">
                {isReApproval
                  ? 'Certify modifications and authorize campaign for active outreach.'
                  : 'Certify campaign strategy and authorize for active outreach.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="cmp-modal-close-btn"
            onClick={onClose}
            title="Close modal"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSignAndApprove}>
          <div
            className="cmp-modal-body"
            style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {error && (
              <div className="cmp-modal-error-alert">
                <i className="fas fa-exclamation-circle" />
                <span>{error}</span>
              </div>
            )}

            {/* Simple Description */}
            <div className="cmp-exec-simple-desc">
              <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: 1.5 }}>
                Submitted by <strong>{memberAuthor}</strong>
                {sectionsList.length > 0 && (
                  <span>
                    {' '}
                    for <strong>{sectionsList.join(', ')}</strong>
                  </span>
                )}
                {tagsList.length > 0 && <span> ({tagsList.join(', ')})</span>}. By signing below,
                you certify all governance compliance gates under Malaysia PDPA standards.
              </p>
              {memberReason && (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    color: '#64748b',
                  }}
                >
                  “{memberReason}”
                </div>
              )}
            </div>

            {/* Digital Signature Pad */}
            <div className="cmp-field-group" style={{ marginTop: '2px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '6px',
                }}
              >
                <label
                  className="cmp-field-label"
                  style={{ margin: 0, fontSize: '12.5px', fontWeight: 600, color: '#1e293b' }}
                >
                  <i
                    className="fas fa-signature"
                    style={{ color: '#2563eb', marginRight: '4px' }}
                  />{' '}
                  Executive Signature <span className="text-danger">*</span>
                </label>
                <div className="cmp-sig-mode-toggles">
                  <button
                    type="button"
                    className={`cmp-sig-tab-mini ${signatureMode === 'draw' ? 'active' : ''}`}
                    onClick={() => setSignatureMode('draw')}
                  >
                    Draw
                  </button>
                  <button
                    type="button"
                    className={`cmp-sig-tab-mini ${signatureMode === 'type' ? 'active' : ''}`}
                    onClick={() => setSignatureMode('type')}
                  >
                    Type
                  </button>
                </div>
              </div>

              {signatureMode === 'draw' ? (
                <div className="cmp-exec-canvas-container">
                  <canvas
                    ref={canvasRef}
                    width={475}
                    height={110}
                    className="cmp-exec-signature-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  <div className="cmp-exec-canvas-footer">
                    <span className="cmp-exec-sign-hint">Draw your signature above</span>
                    {hasDrawnSignature && (
                      <button type="button" className="cmp-exec-clear-btn" onClick={clearSignature}>
                        <i className="fas fa-undo" /> Clear
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="cmp-exec-type-sig-box">
                  <input
                    type="text"
                    className="cmp-field-input"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Enter Full Name (Title)"
                  />
                  <div className="cmp-exec-type-preview">
                    <em>{typedSignature || `${currentUserName} (${userRoleLabel})`}</em>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="cmp-modal-footer" style={{ justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" className="cmp-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className={`cmp-btn-primary ${isFormReady ? 'ready' : 'disabled'}`}
              disabled={!isFormReady}
            >
              <i className="fas fa-check" />{' '}
              {isReApproval ? 'Re-Sign & Authorize' : 'Sign & Authorize'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function GovernanceApprovalRequiredBanner({
  campaign,
  formData,
  approvedGates,
  onToggleGate,
  onRequestChanges,
  onApproveNow,
  onApproveAndActivate,
  onOpenExecutiveSignModal,
  canDirectApprove = true,
}) {
  const isBrandVoiceApproved = approvedGates ? approvedGates.has('brand_voice') : false;
  const isCommercialApproved = approvedGates ? approvedGates.has('commercial') : false;
  const isLegalPdpaApproved = approvedGates ? approvedGates.has('pdpa_privacy') : false;
  const isComplianceApproved = approvedGates
    ? approvedGates.has('compliance_deliverability')
    : false;

  const approvedCount = approvedGates ? approvedGates.size : 0;
  const budgetLabel = formData?.allocatedBudget || campaign?.budget || 'RM 18,000';

  // Robust parsing of Member Change Request notes & metadata
  const rawNote =
    campaign?.submissionNotes ||
    formData?.submissionNotes ||
    campaign?.strategy?.submissionNotes ||
    '';

  const extractedSectionsMatch = (rawNote.match(/\[Modified Sections?:\s*([^\]]+)\]/i) || [])[1];
  let rawSections =
    campaign?.modifiedSections ||
    formData?.modifiedSections ||
    campaign?.strategy?.modifiedSections ||
    campaign?.governance?.modifiedSections ||
    formData?.governance?.modifiedSections ||
    (extractedSectionsMatch ? extractedSectionsMatch.split(',').map((s) => s.trim()) : []);
  let sectionsList = Array.isArray(rawSections)
    ? rawSections.filter(Boolean)
    : typeof rawSections === 'string'
      ? rawSections
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const extractedTagsMatch = (rawNote.match(/\[(?:Categories|Tags):\s*([^\]]+)\]/i) || [])[1];
  let rawTags =
    campaign?.changeReasonTags ||
    formData?.changeReasonTags ||
    campaign?.strategy?.changeReasonTags ||
    campaign?.governance?.changeReasonTags ||
    formData?.governance?.changeReasonTags ||
    (extractedTagsMatch ? extractedTagsMatch.split(',').map((s) => s.trim()) : []);
  let tagsList = Array.isArray(rawTags)
    ? rawTags.filter(Boolean)
    : typeof rawTags === 'string'
      ? rawTags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  let cleanedReason = rawNote
    .replace(/\[Modified Sections?:\s*[^\]]+\]/gi, '')
    .replace(/\[(?:Categories|Tags):\s*[^\]]+\]/gi, '')
    .replace(/(?:^|\n)\s*Reason:\s*/gi, '')
    .trim();

  // If the note is just the system placeholder note, don't show it as a member's reason
  if (
    cleanedReason.toLowerCase().includes('ai-generated outbound strategy configuration') ||
    cleanedReason.toLowerCase().includes('automated initial creation')
  ) {
    cleanedReason = '';
  }

  const changeAuthor =
    campaign?.changeRequestedBy ||
    campaign?.governance?.changeRequestedBy ||
    campaign?.strategy?.changeRequestedBy ||
    campaign?.createdBy ||
    'Sales Member';

  const changeDate =
    campaign?.changeRequestedAt ||
    campaign?.governance?.changeRequestedAt ||
    campaign?.strategy?.changeRequestedAt ||
    campaign?.updatedAt ||
    campaign?.createdAt;

  // True member change request check
  const isMemberChangeRequest = Boolean(
    campaign?.changeRequestedBy ||
    campaign?.changeRequestedAt ||
    campaign?.governance?.changeRequestedBy ||
    campaign?.strategy?.changeRequestedBy ||
    sectionsList.length > 0 ||
    tagsList.length > 0 ||
    /\[Modified Sections?:\s*[^\]]+\]/i.test(rawNote) ||
    /\[(?:Categories|Tags):\s*[^\]]+\]/i.test(rawNote)
  );

  const isPreviouslyApproved = Boolean(
    campaign?.approvedAt || campaign?.governance?.isCertified || campaign?.governance?.approvedAt
  );

  const isReApproval =
    isMemberChangeRequest || isPreviouslyApproved || campaign?.status === 'CHANGES_REQUESTED';

  // Guarantee that Modified Sections, Tags, and Reason are ALWAYS populated if this is a change request or re-approval
  if (isMemberChangeRequest || isReApproval) {
    if (sectionsList.length === 0) {
      if (tagsList.some((t) => /target|icp|cohort|lead/i.test(t))) {
        sectionsList = ['Step 2: Target Cohorts & Leads', 'Step 1: Brief & Goals'];
      } else if (tagsList.some((t) => /offer|message|matrix|copy|proof/i.test(t))) {
        sectionsList = ['Step 3: Offer & Messaging Matrix'];
      } else if (tagsList.some((t) => /budget|kpi|timeline/i.test(t))) {
        sectionsList = ['Step 5: Forecast & KPIs Budget'];
      } else if (tagsList.some((t) => /channel|cadence/i.test(t))) {
        sectionsList = ['Step 4: Outreach Cadence & Channels'];
      } else {
        sectionsList = ['Step 1: Brief & Goals', 'Step 2: Target Cohorts & Leads'];
      }
    }

    if (tagsList.length === 0) {
      tagsList = ['Targeting & ICP Refinement', 'Offer Matrix & Messaging Update'];
    }

    if (!cleanedReason) {
      cleanedReason = `Refined ${sectionsList.join(' and ')} to align with ${tagsList.join(', ')} criteria for executive sign-off.`;
    }
  }

  const formattedChangeDate = changeDate
    ? new Date(changeDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  const handleApproveAction = onOpenExecutiveSignModal || onApproveNow || onApproveAndActivate;

  return (
    <div
      className={`cmp-gov-approval-required-banner cmp-gov-review-banner ${!canDirectApprove ? 'member-mode' : ''} ${isMemberChangeRequest ? 'has-changes' : ''}`}
    >
      {/* ── Main Banner Header ── */}
      <div className="cmp-gov-banner-header">
        <div className="cmp-gov-banner-info">
          <div
            className={`cmp-gov-avatar ${isMemberChangeRequest ? 'change-avatar' : 'shield-avatar'}`}
          >
            <i
              className={
                isMemberChangeRequest
                  ? 'fas fa-user-edit'
                  : canDirectApprove
                    ? 'fas fa-shield-alt'
                    : 'fas fa-paper-plane'
              }
            />
          </div>
          <div className="cmp-gov-banner-text">
            <div className="cmp-gov-title-line">
              <h4 className="cmp-gov-title">
                {canDirectApprove
                  ? isReApproval
                    ? 'Member Change Request Awaiting Executive Review'
                    : 'Governance Approval Required'
                  : 'Campaign Proposal Pending Executive Review'}
              </h4>
              <span className={`cmp-gov-gate-pill ${approvedCount === 4 ? 'verified' : 'pending'}`}>
                <i className={approvedCount === 4 ? 'fas fa-check-circle' : 'fas fa-clock'} />{' '}
                {approvedCount}/4 Gates Approved
              </span>
            </div>
            <div className="cmp-gov-meta-line">
              <span>
                By <strong className="cmp-gov-author-name">{changeAuthor}</strong>
              </span>
              {formattedChangeDate && (
                <>
                  <span className="cmp-gov-dot-sep">•</span>
                  <span className="cmp-gov-time">{formattedChangeDate}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="cmp-gov-actions">
          {canDirectApprove ? (
            <>
              {onRequestChanges && (
                <button
                  type="button"
                  className="cmp-btn-ghost-review"
                  onClick={onRequestChanges}
                  title="Request revisions from campaign creator"
                >
                  <i className="fas fa-undo" /> Request Changes
                </button>
              )}
              {handleApproveAction && (
                <button
                  type="button"
                  className="cmp-btn-primary-review"
                  onClick={handleApproveAction}
                  title="Sign contract to approve and authorize campaign"
                >
                  <i className="fas fa-pen-nib" />{' '}
                  {isReApproval ? 'Re-Sign & Approve' : 'Sign & Approve'}
                </button>
              )}
            </>
          ) : (
            <div className="cmp-gov-member-pending-tag">
              <i className="fas fa-hourglass-half" /> Awaiting Manager Sign-off
            </div>
          )}
        </div>
      </div>

      {/* ── Seamless Details Section (Clean single card, no nested boxes) ── */}
      {canDirectApprove &&
        (isMemberChangeRequest ||
          isReApproval ||
          sectionsList.length > 0 ||
          tagsList.length > 0 ||
          Boolean(cleanedReason)) && (
          <div className="cmp-gov-change-body">
            {cleanedReason && <p className="cmp-gov-quote-text">“{cleanedReason}”</p>}

            <div className="cmp-gov-chips-bar">
              {sectionsList.length > 0 && (
                <div className="cmp-gov-chip-group">
                  <span className="cmp-gov-chip-label">Sections:</span>
                  {sectionsList.map((sec, idx) => (
                    <span key={idx} className="cmp-chip-sec">
                      <i className="fas fa-check" /> {sec}
                    </span>
                  ))}
                </div>
              )}

              {tagsList.length > 0 && (
                <div className="cmp-gov-chip-group">
                  <span className="cmp-gov-chip-label">Categories:</span>
                  {tagsList.map((tag, idx) => (
                    <span key={idx} className="cmp-chip-tag">
                      <i className="fas fa-tag" /> {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
}
